import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, TriangleAlert } from 'lucide-react';
import type { WorkflowSummary } from '@vagusx/clients';
import { WORKFLOW_PURPOSES } from '@vagusx/shared';
import { SortableHeader, TablePage } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useWorkflows } from '@/hooks/use-workflows';
import { NewWorkflowDialog } from './NewWorkflowDialog';

const PURPOSE_LABELS = new Map(WORKFLOW_PURPOSES.map((p) => [p.intent, p.label]));

const columns: ColumnDef<WorkflowSummary>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>Workflow</SortableHeader>,
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="font-medium">{row.original.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {row.original.intent
            ? (PURPOSE_LABELS.get(row.original.intent) ?? 'No call runs this yet')
            : 'No call runs this yet'}
        </div>
      </div>
    ),
  },
  {
    id: 'kind',
    header: 'Kind',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {row.original.direction === 'inbound' ? 'Inbound' : 'Outbound'} ·{' '}
        {row.original.channel === 'voice' ? 'phone' : row.original.channel}
      </span>
    ),
  },
  {
    id: 'live',
    header: 'Live',
    cell: ({ row }) => {
      const { activeVersion, hasUnpublishedChanges } = row.original;
      return (
        <div className="flex items-center gap-2">
          {activeVersion ? (
            <Badge variant="secondary">Version {activeVersion.version}</Badge>
          ) : (
            <Badge variant="outline">Not published</Badge>
          )}
          {hasUnpublishedChanges && activeVersion && (
            <span className="text-xs text-muted-foreground">Unpublished changes</span>
          )}
        </div>
      );
    },
  },
  {
    id: 'problems',
    header: 'Problems',
    cell: ({ row }) =>
      row.original.problems.errors > 0 ? (
        <span className="flex items-center gap-1 text-sm text-destructive">
          <TriangleAlert className="h-3.5 w-3.5" />
          {row.original.problems.errors}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">None</span>
      ),
  },
];

export function WorkflowList({ onOpen }: { onOpen: (id: string) => void }): React.ReactElement {
  const { data, isLoading, error } = useWorkflows();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <TablePage
        title="Workflows"
        description="What the agent says on a call, in what order, and when each step runs."
        columns={columns}
        data={data ?? []}
        total={data?.length}
        isLoading={isLoading}
        error={error}
        searchColumn="name"
        searchPlaceholder="Search workflows..."
        onRowClick={(row) => onOpen(row.id)}
        emptyMessage="No workflows yet."
        errorMessage="Failed to load workflows."
        headerAction={
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New workflow
          </Button>
        }
      />
      <NewWorkflowDialog open={creating} onOpenChange={setCreating} onCreated={onOpen} />
    </>
  );
}
